import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["promptThread"];
// @ts-expect-error Not typed yet
import I18n from 'I18n';
import { useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
// @ts-expect-error Not typed yet
import { getContentModelId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
// @ts-expect-error Untyped import
import { memo, useRef } from 'react';
// @ts-expect-error Untyped import
import bus from 'ContentEditorUI/coffee/lib/eventBus';
import { useCollaborationSession } from 'copilot-toolkit/hooks/useCollaborationSession';
import { getCopilotActionFromContentAssistantData, getContentAssistantContextAsStringForCopilot } from 'copilot-toolkit/utils/contentAssistantUtils';
const TIMER = 100;
const formatParameters = parameters => {
  const {
      promptThread
    } = parameters,
    rest = _objectWithoutPropertiesLoose(parameters, _excluded);
  return Object.assign({}, rest, {
    promptThread: promptThread ? I18n.text(promptThread) : ''
  });
};

// Dealing with complexity of of multiple tinymce instances and async events. Some interesting discussion
// and possible other options in https://git.hubteam.com/HubSpot/ContentEditorUI/pull/18336/files#r3248731
export const useThrottleIfTrueDebounceIfFalse = (methodToThrottleOrDebounce, getHasMessageMetCondition) => {
  const handler = useRef(null);
  return useCallback(message => {
    const hasMetCondition = getHasMessageMetCondition(message);
    // Throttle if the condition is met
    if (hasMetCondition) {
      methodToThrottleOrDebounce(message);
      if (handler.current) {
        clearTimeout(handler.current);
      }
      handler.current = setTimeout(() => {
        handler.current = null; // Reset the handler
      }, TIMER);
    }
    // Debounce if the condition is not met and the throttle isn't running
    if (!hasMetCondition && !handler.current) {
      handler.current = setTimeout(() => {
        methodToThrottleOrDebounce(message);
      }, TIMER);
    }
  }, [getHasMessageMetCondition, methodToThrottleOrDebounce]);
};
const sendToTinymceActionToParentOrInpage = ({
  action,
  payload
}) => {
  const isUsingTinyMCEInpage = !(window.tinymce && window.tinymce.activeEditor);
  if (isUsingTinyMCEInpage) {
    // Trigger in the parent frame from inpage preview frame (note, uses different action name)
    bus.postMessage({
      action: `${action}:inpage`,
      payload
    });
  } else {
    // Trigger in the same (parent) frame
    bus.trigger(`post:${action}`, {
      payload
    });
  }
};
const CopilotSessionManager = ({
  appKey
}) => {
  const contentId = useSelector(getContentModelId);
  const currentContextRef = useRef(null);
  const handleEditorAction = useCallback((action = {}) => {
    switch (action.payload.actionType) {
      case 'insert':
        sendToTinymceActionToParentOrInpage({
          action: 'insertIntoRichText',
          payload: {
            text: action.payload.html
          }
        });
        break;
      default:
        console.error('Unknown action type', action.payload.actionType);
    }
  }, []);
  const handleGetCurrentContext = useCallback(() => {
    return currentContextRef.current;
  }, []);
  const {
    sendActionFromAppToCopilot,
    sendCollaborationStateChangeFromAppToCopilot
  } = useCollaborationSession({
    onCopilotAction: handleEditorAction,
    onGetCurrentSessionContext: handleGetCurrentContext,
    sessionId: contentId,
    appName: `${appKey}:ContentEditorUI`,
    plugins: ['EditorPlugin']
  });
  const sendTinyMCEStateToCopilot = useCallback(message => {
    const {
      hasFocus,
      hasSelection,
      editorContext,
      currentSelection
    } = message.payload;
    const collaborationContext = getContentAssistantContextAsStringForCopilot({
      editorContext,
      selectedText: hasSelection ? currentSelection : ''
    });
    currentContextRef.current = collaborationContext;
    // Send the current state to Copilot
    sendCollaborationStateChangeFromAppToCopilot({
      hasFocus,
      hasSelection
    });
  }, [sendCollaborationStateChangeFromAppToCopilot]);
  const handleThrottledOrDebouncedStateUpdate = useThrottleIfTrueDebounceIfFalse(sendTinyMCEStateToCopilot, message => {
    const {
      hasFocus
    } = message.payload;
    return hasFocus;
  });
  useEffect(() => {
    bus.on('post:copilot:send-copilot-command', message => {
      var _command$parameters;
      const {
        command,
        editorContext,
        currentSelection,
        hasSelection
      } = message.payload;
      let messageText = I18n.text(command.labelKey);
      if ((_command$parameters = command.parameters) !== null && _command$parameters !== void 0 && _command$parameters.toneOfVoice) {
        // Note: this will only work in english.  We really need to map everything over in next iteration!
        messageText = `${messageText} to ${command.parameters.toneOfVoice}`;
      }
      const contentAssistantData = {
        commandId: command.commandId || command.id,
        parameters: formatParameters(command.parameters),
        editorContext,
        selectedText: hasSelection ? currentSelection : ''
      };
      const collaborationContext = getContentAssistantContextAsStringForCopilot(contentAssistantData);
      currentContextRef.current = collaborationContext;
      sendCollaborationStateChangeFromAppToCopilot({
        hasFocus: true,
        hasSelection
      });
      const messageAction = getCopilotActionFromContentAssistantData({
        messageText,
        contentAssistantData
      });
      sendActionFromAppToCopilot(messageAction);
    });

    // The debouncing happens here, as instances are sending the same message
    bus.on('post:copilot:update-tiny-mce-state', handleThrottledOrDebouncedStateUpdate);
    // The debouncing happens in the hook
    bus.on('post:copilot:update-selection', sendTinyMCEStateToCopilot);
    return () => {
      bus.off('post:copilot:send-copilot-command');
      bus.off('post:copilot:update-tiny-mce-state');
      bus.off('post:copilot:update-selection');
    };
  }, [sendCollaborationStateChangeFromAppToCopilot, sendActionFromAppToCopilot, sendTinyMCEStateToCopilot, handleThrottledOrDebouncedStateUpdate]);
  return null;
};
export default /*#__PURE__*/memo(CopilotSessionManager);